import {
  rescueEscortDefaults,
  rescueRadioDefaults,
  rescuesDefaults,
  rescueWavesDefaults,
} from '../s2/defaults';
import type { RescueRulesDef, RescueScenarioDef, RescueFreeDef } from '../s2/types';
import type { Err, Ids } from './common';
import { filled, idsOf, rangeMsg, sectionList } from './s2common';
import type { Bundle } from './types';

/** `RescueRules` vocabulary. */
export const RESCUE_VARS = ['trapped.bitten', 'trapped.second', 'trapped.bait'] as const;

/**
 * Port of shared-cs `ContentValidator.Rescues.cs` (`RescueSections`: rescues and worldEvents.types[rescue_call]), S2-CR. Same errors,
 * same wording, same order. A rescue plays on a scenario (ADR 0034): a free and an escort objective, an actor per trapped slot, no
 * recruit action, and a free objective whose holdS is at most free.seconds × minFreeMult.
 */
export function validateRescues(b: Bundle, err: Err, warn: Err, ids: Ids): void {
  void warn;
  void ids;
  const S = 'rescues';
  const W = 'rescues';
  const r = filled(rescuesDefaults(), (b.rescues ?? {}) as RescueRulesDef);
  const at = (p: string, m: string) => err(S, undefined, p, m);
  const range = (
    v: number,
    min: number,
    max: number,
    where: string,
    field: string,
    path: string,
  ) => {
    const m = rangeMsg(v, min, max, where, field);
    if (m) at(path, m);
  };
  if (r.energySurcharge < 0 || r.energySurcharge > 100)
    at('energySurcharge', `${W} needs energySurcharge in 0..100.`);
  if (r.successCooldownHours < 0 || r.successCooldownHours > 168)
    at('successCooldownHours', `${W} needs successCooldownHours in 0..168.`);
  if (r.successesPer7Days < 0 || r.successesPer7Days > 20)
    at('successesPer7Days', `${W} needs successesPer7Days in 0..20.`);
  if (r.claimHoldHours < 1 || r.claimHoldHours > 168)
    at('claimHoldHours', `${W} needs claimHoldHours in 1..168.`);
  range(r.traitChance, 0, 1, W, 'traitChance', 'traitChance');
  range(r.minFreeMult, 0.1, 1, W, 'minFreeMult', 'minFreeMult');
  if (r.waveWarnS < 0 || r.waveWarnS > 30) at('waveWarnS', `${W} needs waveWarnS in 0..30.`);
  const radio = filled(rescueRadioDefaults(), r.radio ?? undefined);
  for (const n of radio.callsPerDayByLevel ?? [])
    if (n < 0 || n > 10)
      at('radio.callsPerDayByLevel', `${W}.radio.callsPerDayByLevel entries must be 0..10.`);
  if (radio.shortHandedCallsPerDay < 0 || radio.shortHandedCallsPerDay > 10)
    at('radio.shortHandedCallsPerDay', `${W}.radio needs shortHandedCallsPerDay in 0..10.`);
  if (radio.callMinutes < 10 || radio.callMinutes > 1440)
    at('radio.callMinutes', `${W}.radio needs callMinutes in 10..1440.`);
  if (radio.autoSpawnGapMinutes < 0 || radio.autoSpawnGapMinutes > 1440)
    at('radio.autoSpawnGapMinutes', `${W}.radio needs autoSpawnGapMinutes in 0..1440.`);
  const e = filled(rescueEscortDefaults(), r.escort ?? undefined);
  range(e.speedMult, 0.2, 1.5, `${W}.escort`, 'speedMult', 'escort.speedMult');
  range(e.injuredSpeedMult, 0.2, 1.5, `${W}.escort`, 'injuredSpeedMult', 'escort.injuredSpeedMult');
  range(e.leashM, 2, 30, `${W}.escort`, 'leashM', 'escort.leashM');
  range(e.targetWeight, 0, 5, `${W}.escort`, 'targetWeight', 'escort.targetWeight');
  range(e.unstickS, 1, 30, `${W}.escort`, 'unstickS', 'escort.unstickS');

  for (const [key, names] of Object.entries(r.namePools ?? {}))
    if (names == null || !names.some((n) => (n ?? '').trim() !== ''))
      at(`namePools.${key}`, `${W} name pool '${key}' is empty.`);

  const scenarios = sectionList<RescueScenarioDef>(
    r.scenarios,
    `${W}.scenarios`,
    err,
    S,
    'scenarios',
  );
  const scenarioIds = idsOf(scenarios, (s) => s.id, 'rescue scenario', err, S, 'scenarios');
  for (const [s, i] of scenarios)
    rescueScenario(b, r, s, (p, m) => at(`scenarios.${i}${p ? `.${p}` : ''}`, m));

  (b.worldEvents?.types ?? []).forEach((t, j) => {
    if (t == null) return;
    const tx = t as typeof t & { rescueScenarioIds?: string[] | null };
    for (const id of tx.rescueScenarioIds ?? [])
      if (!scenarioIds.has(id))
        err(
          'worldEvents',
          undefined,
          `types.${j}.rescueScenarioIds`,
          `World event type '${t.id ?? ''}' rescue scenario '${id}' doesn't exist.`,
        );
    if (
      t.id === 'rescue_call' &&
      (tx.rescueScenarioIds == null || tx.rescueScenarioIds.length === 0) &&
      scenarios.length === 0
    )
      err(
        'worldEvents',
        undefined,
        `types.${j}.rescueScenarioIds`,
        "World event type 'rescue_call' needs rescue scenarios.",
      );
  });
}

const fmt1 = (v: number) => String(Math.round(v * 10) / 10);

function rescueScenario(
  b: Bundle,
  r: Required<RescueRulesDef>,
  s: RescueScenarioDef,
  at: (path: string, m: string) => void,
) {
  const where = `Rescue '${s.id ?? ''}'`;
  if (!(s.title ?? '').trim()) at('title', `${where} needs a title.`);
  for (const cat of s.categoryIds ?? [])
    if (!(b.categories ?? []).some((x) => x != null && x.id === cat))
      at('categoryIds', `${where} category '${cat}' doesn't exist.`);
  for (const tag of s.osmTagsAny ?? [])
    if (!tag || tag.indexOf('=') <= 0)
      at('osmTagsAny', `${where} OSM tag '${tag ?? ''}' must be key=value.`);
  if (s.setPieceRoomId && !(b.setPieces ?? []).some((x) => x != null && x.id === s.setPieceRoomId))
    at('setPieceRoomId', `${where} set piece '${s.setPieceRoomId}' doesn't exist.`);

  const free = filled(
    { kind: '', seconds: 60, toolTag: '', toolMult: 1, secondSeconds: 0 },
    (s.free ?? undefined) as RescueFreeDef | undefined,
  );
  if (!(free.seconds > 0 && free.seconds <= 600))
    at('free.seconds', `${where} needs free.seconds above 0 and at most 600.`);
  const tm = rangeMsg(free.toolMult, 0.1, 1, where, 'free.toolMult');
  if (tm) at('free.toolMult', tm);
  if (free.secondSeconds < 0 || free.secondSeconds > 300)
    at('free.secondSeconds', `${where} needs free.secondSeconds in 0..300.`);

  const w = filled(rescueWavesDefaults(), s.waves ?? undefined);
  if (
    w.firstAtS < 0 ||
    w.everyS <= 0 ||
    w.baseSize < 0 ||
    w.growth < 0 ||
    w.perExtraMember < 0 ||
    w.escortEveryS < 0 ||
    w.escortSize < 0
  )
    at('waves', `${where} wave numbers can't be negative, and everyS must be above 0.`);
  if (
    w.finalExtraZombieId &&
    !(b.zombies ?? []).some((z) => z != null && z.id === w.finalExtraZombieId)
  )
    at(
      'waves.finalExtraZombieId',
      `${where} finalExtraZombieId '${w.finalExtraZombieId}' doesn't exist.`,
    );

  const trapped = s.trapped ?? [];
  if (trapped.length === 0) at('trapped', `${where} needs at least one trapped person.`);
  const slots = new Set<string>();
  trapped.forEach((t, k) => {
    if (t == null) return;
    const p = (f: string) => `trapped.${k}.${f}`;
    const slot = t.slot ?? '';
    const tw = `${where} trapped '${slot}'`;
    if (slot !== 'main' && slot !== 'second') at(p('slot'), `${tw} slot must be main or second.`);
    if (slots.has(slot)) at(p('slot'), `${where} has two trapped '${slot}' slots.`);
    slots.add(slot);
    if (!Object.prototype.hasOwnProperty.call(r.namePools ?? {}, t.namePool ?? ''))
      at(p('namePool'), `${tw} name pool '${t.namePool ?? ''}' doesn't exist.`);
    if (t.roleId && !(b.backgrounds ?? []).some((x) => x != null && x.id === t.roleId))
      at(p('roleId'), `${tw} background '${t.roleId}' doesn't exist.`);
    if (t.traitId && !(b.survivorTraits ?? []).some((x) => x != null && x.id === t.traitId))
      at(p('traitId'), `${tw} trait '${t.traitId}' doesn't exist.`);
    const hp = t.startHealth ?? 100;
    if (hp < 1 || hp > 200) at(p('startHealth'), `${tw} needs startHealth in 1..200.`);
    const ic = rangeMsg(t.injuredChance ?? 0, 0, 1, tw, 'injuredChance');
    if (ic) at(p('injuredChance'), ic);
  });
  if (!slots.has('main')) at('trapped', `${where} needs a 'main' trapped slot.`);
  if (free.secondSeconds > 0 && !slots.has('second'))
    at('free.secondSeconds', `${where} has free.secondSeconds but no 'second' trapped slot.`);

  const beats = s.beats ?? {};
  const chainExists = (id: string | null | undefined) =>
    !!id && (b.beatChains ?? []).some((c) => c != null && c.id === id);
  if (beats.briefing && !chainExists(beats.briefing))
    at('beats.briefing', `${where} briefing chain '${beats.briefing}' doesn't exist.`);
  let twists = 0;
  const vars = new Set<string>();
  (beats.twists ?? []).forEach((t, k) => {
    if (t == null) return;
    twists++;
    const p = (f: string) => `beats.twists.${k}.${f}`;
    const tw = `${where} twist '${t.var ?? ''}'`;
    if (!chainExists(t.beatId)) at(p('beatId'), `${tw} chain '${t.beatId ?? ''}' doesn't exist.`);
    if (!(RESCUE_VARS as readonly string[]).includes(t.var ?? ''))
      at(p('var'), `${tw} var must be trapped.bitten, trapped.second or trapped.bait.`);
    if (vars.has(t.var ?? '')) at(p('var'), `${where} rolls '${t.var ?? ''}' twice.`);
    vars.add(t.var ?? '');
    let sum = 0;
    for (const [key, v] of Object.entries(t.weights ?? {})) {
      if (v < 0) at(p('weights'), `${tw} weight '${key}' can't be negative.`);
      sum += Math.max(0, v);
    }
    if (sum <= 0) at(p('weights'), `${tw} needs a positive weight.`);
    if (t.var === 'trapped.second' && !slots.has('second'))
      at(p('var'), `${tw} needs a 'second' trapped slot.`);
  });
  if (twists > 3) at('beats.twists', `${where} has more than 3 twists.`);

  const scenario = s.scenarioId
    ? (b.scenarios ?? []).find((x) => x != null && x.id === s.scenarioId)
    : undefined;
  if (!scenario) {
    at('scenarioId', `${where} scenario '${s.scenarioId ?? ''}' doesn't exist.`);
    return;
  }
  const sw = `${where} scenario '${scenario.id}'`;
  const freeObj = (scenario.objectives ?? []).find((o) => o != null && o.type === 'free');
  const floor = free.seconds * Math.max(0.1, r.minFreeMult);
  if (!freeObj) at('scenarioId', `${sw} needs a free objective.`);
  else if ((freeObj.holdS ?? 0) > floor + 0.001)
    at(
      'scenarioId',
      `${sw} free objective holdS must be at most free.seconds × minFreeMult (${fmt1(floor)}).`,
    );
  if (!(scenario.objectives ?? []).some((o) => o != null && o.type === 'escort'))
    at('scenarioId', `${sw} needs an escort objective.`);
  for (const slot of slots)
    if (!(scenario.actors ?? []).some((a) => a != null && a.id === slot))
      at('scenarioId', `${sw} needs an actor '${slot}'.`);
  let recruits = (scenario.onWin ?? []).some((a) => a != null && a.type === 'recruit');
  for (const t of scenario.triggers ?? [])
    if ((t?.do ?? []).some((a) => a != null && a.type === 'recruit')) recruits = true;
  if (recruits)
    at(
      'scenarioId',
      `${sw} can't use a recruit action (the rescue makes its recruits from the ticket).`,
    );
}
